/**
 * iter234 · LIVE INTELLIGENCE panel (spec 29A + 33).
 *
 * A. RM & ORDER FULFILMENT WATCH — real names, real numbers, real actions:
 *      WHAT is at risk · WHY (which material, how short) · IMPACT (the date
 *      it slips to) · ACTION (reserve · buy · move · ask TI).
 *    Two lenses: by production plan, or by customer order.
 * B. The question chips hand off to the MAIN Hey TI assistant (spec 28) —
 *    this panel never tries to be its own little chat box.
 */
import React, { useState } from 'react';
import {
  Sparkles, AlertTriangle, Info, CheckCircle2, Lock, ShoppingCart,
  CalendarDays, MessageSquareText,
} from 'lucide-react';
import { fmtQty } from '../../utils/format';

const TONE = {
  critical: 'text-red-300', warn: 'text-orange-300',
  info: 'text-zinc-400', ok: 'text-emerald-300',
};

const LEVEL_BADGE = {
  short: 'bg-red-500/15 text-red-300 border-red-500/30',
  partial: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
};

export const PLANNING_QUESTIONS = [
  'What should we produce tomorrow?',
  'Which orders are at risk this week and why?',
  'What is the earliest date we can actually produce, based on vendor ETA and QC clearance?',
  'Which raw materials should I buy today so next week’s plan is safe?',
  'Rebuild my dispatch picture — what is ready, awaiting production and already dispatched?',
  'Warn me if a product is planned right after one that needs the line washed first.',
];

export default function PlanningWatchPanel({ data, onAsk, onReserve, onBuy, onMove, onPlanRequirement }) {
  const [mode, setMode] = useState('plan');
  const plans = data?.watch_plans || [];
  const orders = data?.watch_orders || [];
  const lines = data?.watch || [];

  return (
    <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/20 rounded-xl p-3 space-y-2.5"
      data-testid="fp-pw-intel">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles size={14} className="text-amber-400" />
        <span className="text-xs font-heading font-semibold text-amber-300">RM & order fulfilment watch</span>
        <div className="flex items-center gap-1 bg-zinc-800/70 rounded-lg p-0.5 ml-1">
          {[{ v: 'plan', l: 'By plan' }, { v: 'order', l: 'By order' }].map(m => (
            <button key={m.v} onClick={() => setMode(m.v)} data-testid={`fp-pw-watch-mode-${m.v}`}
              className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${mode === m.v ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-100'}`}>
              {m.l}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-0.5" data-testid="fp-pw-watch">
        {lines.length === 0 && <p className="text-[11px] text-zinc-500">Nothing to flag in this period.</p>}
        {lines.map((w, i) => (
          <p key={i} className={`text-[11px] flex items-start gap-1.5 ${TONE[w.level]}`}>
            {w.level === 'critical' ? <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              : w.level === 'ok' ? <CheckCircle2 size={11} className="mt-0.5 shrink-0" />
                : <Info size={11} className="mt-0.5 shrink-0" />}
            <span>{w.text}</span>
          </p>
        ))}
      </div>

      {mode === 'plan' && plans.length > 0 && (
        <div className="space-y-1.5" data-testid="fp-pw-watch-plans">
          {plans.map(p => (
            <div key={p.plan_id} className="ti-surface-inset border border-zinc-800 rounded-lg px-2.5 py-2"
              data-testid={`fp-pw-watch-plan-${p.plan_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-[240px]">
                  <p className="text-[11px] text-zinc-100">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border mr-1.5 ${LEVEL_BADGE[p.level]}`}>
                      {p.level === 'short' ? 'CANNOT MAKE' : 'AT RISK'}
                    </span>
                    <span className="font-semibold">{p.product_name}</span>
                    {' · '}{p.customer_name}
                    {p.sales_order_number ? ` · SO ${p.sales_order_number}` : ''}
                    {' · '}{fmtQty(p.quantity, 'kg')} on {p.planned_date}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    <span className="text-zinc-500">Why:</span>{' '}
                    {p.short_materials.length
                      ? p.short_materials.map(s => `${s.name} short ${fmtQty(s.qty)} ${s.unit}${s.eta ? ` (arrives ${s.eta})` : ' (nothing on order)'}`).join(' · ')
                      : 'raw material not covered'}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${(p.slips || p.blocked_by?.length) ? 'text-orange-300' : 'text-zinc-400'}`}>
                    <span className="text-zinc-500">Impact:</span>{' '}
                    {p.blocked_by?.length
                      ? `no feasible date yet — ${p.blocked_by.join(', ')} is not on order anywhere`
                      : p.slips
                        ? `slips to ${p.earliest_feasible_date} (earliest feasible, after inward + QC)`
                        : 'can still run on the planned date if reserved now'}
                    {p.required_date ? ` · customer needs it by ${p.required_date}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Act t="Reserve RM now" onClick={() => onReserve(p.plan_id)} testid={`fp-pw-watch-reserve-${p.plan_id}`}>
                    <Lock size={11} /> Reserve
                  </Act>
                  <Act t="Draft purchase order for the shortfall" onClick={() => onBuy(p.plan_id)} testid={`fp-pw-watch-buy-${p.plan_id}`}>
                    <ShoppingCart size={11} /> Buy
                  </Act>
                  <Act t={p.earliest_feasible_date ? `Move production to ${p.earliest_feasible_date}` : 'Move production date'}
                    onClick={() => onMove(p.plan_id, p.earliest_feasible_date)} testid={`fp-pw-watch-move-${p.plan_id}`}>
                    <CalendarDays size={11} /> Move
                  </Act>
                  <Act t="Ask Hey TI about this" onClick={() => onAsk(`Why can't we make ${p.product_name} on ${p.planned_date}? What are the options?`)}
                    testid={`fp-pw-watch-ask-${p.plan_id}`}>
                    <MessageSquareText size={11} /> Ask TI
                  </Act>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'order' && orders.length > 0 && (
        <div className="space-y-1.5" data-testid="fp-pw-watch-orders">
          {orders.map((o, i) => (
            <div key={o.sales_order_id || i} className="ti-surface-inset border border-zinc-800 rounded-lg px-2.5 py-2"
              data-testid={`fp-pw-watch-order-${o.sales_order_id || i}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-[240px]">
                  <p className="text-[11px] text-zinc-100">
                    <span className="font-semibold">{o.customer_name}</span>
                    {o.sales_order_number ? ` · SO ${o.sales_order_number}` : ''}
                    {o.required_date ? ` · needed ${o.required_date}` : ''}
                  </p>
                  {o.at_risk_products.length > 0 && (
                    <p className="text-[11px] text-orange-300 mt-0.5">
                      {o.at_risk_products.length} of {o.products.length} product(s) at risk: {o.at_risk_products.join(', ')}
                    </p>
                  )}
                  {o.needs_planning.length > 0 && (
                    <p className="text-[11px] text-red-300 mt-0.5">
                      No production slot yet: {o.needs_planning.map(n => `${n.product_name} ${fmtQty(n.qty)} kg`).join(' · ')}
                    </p>
                  )}
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    🔴 {fmtQty(o.left, 'kg')} left · planned {fmtQty(o.planned, 'kg')}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {o.needs_planning.length > 0 && (
                    <Act t="Plan the missing quantity" onClick={() => onPlanRequirement(o)} testid={`fp-pw-watch-plan-order-${o.sales_order_id || i}`}>
                      <CalendarDays size={11} /> Plan it
                    </Act>
                  )}
                  <Act t="Ask Hey TI about this order"
                    onClick={() => onAsk(`Can we fulfil ${o.customer_name}'s order ${o.sales_order_number || ''} on time? What is missing?`)}
                    testid={`fp-pw-watch-ask-order-${o.sales_order_id || i}`}>
                    <MessageSquareText size={11} /> Ask TI
                  </Act>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {PLANNING_QUESTIONS.map((c, i) => (
          <button key={i} onClick={() => onAsk(c)} data-testid={`fp-pw-ai-chip-${i}`}
            className="ti-btn-ghost text-[10px] rounded-full px-2.5 py-1 hover:border-amber-500/50 hover:text-amber-300">
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

function Act({ children, onClick, t, testid }) {
  return (
    <button title={t} onClick={onClick} data-testid={testid}
      className="text-[10px] border border-zinc-700 hover:border-amber-500/50 text-zinc-300 hover:text-amber-300 rounded-lg px-2 py-1 flex items-center gap-1">
      {children}
    </button>
  );
}
